// app/javascript/controllers/dashboard/webhooks_controller.js

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "urlInput",
        "webhookList",
        "errorMessage",
        "errorText",
        "emptyState",
        "addButton",
        "environmentSelect"
    ]

    static values = {
        addUrl: String
    }

    connect() {
        this.updateEmptyState()
    }

    handleKeyPress(event) {
        if (event.key === "Enter") {
            event.preventDefault()
            this.addWebhook(event)
        }
    }

    async addWebhook(event) {
        event.preventDefault()
        const url = this.urlInputTarget.value.trim()
        const environment = this.environmentSelectTarget.value

        if (!environment) {
            this.showError(window.I18n.translations['dashboard.setting.webhook.js.select_environment'])
            return
        }

        if (!url) {
            this.showError(window.I18n.translations['dashboard.setting.webhook.js.enter_webhook_url'])
            return
        }

        if (!this.validateUrl(url)) {
            this.showError(window.I18n.translations['dashboard.setting.webhook.js.enter_valid_url'])
            return
        }

        if (this.hasExistingWebhook(url)) {
            this.showError(window.I18n.translations['dashboard.setting.webhook.js.url_already_exists'])
            return
        }

        // Add temporary webhook with loading state
        const tempWebhook = this.createLoadingWebhook(url, environment)
        this.webhookListTarget.insertAdjacentHTML('beforeend', tempWebhook)

        // Disable input and button during request
        this.addButtonTarget.disabled = true
        this.urlInputTarget.disabled = true
        this.environmentSelectTarget.disabled = true

        try {
            const response = await fetch(this.addUrlValue, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
                },
                body: JSON.stringify({ url, environment })
            })

            if (!response.ok) throw new Error('Network response was not ok')

            const data = await response.json()

            // Replace loading webhook with actual data
            const webhookElement = this.webhookListTarget.querySelector(`[data-url="${url}"]`)
            if (webhookElement) {
                webhookElement.outerHTML = this.createWebhookHtml(data)
            }

            this.urlInputTarget.value = ""
            this.environmentSelectTarget.value = "" // Reset environment select
            this.hideError()
        } catch (error) {
            this.showError(window.I18n.translations['dashboard.setting.webhook.js.unable_to_add'])
            // Remove temporary webhook
            const webhookElement = this.webhookListTarget.querySelector(`[data-url="${url}"]`)
            if (webhookElement) {
                webhookElement.remove()
            }
        } finally {
            this.addButtonTarget.disabled = false
            this.urlInputTarget.disabled = false
            this.environmentSelectTarget.disabled = false
            this.updateEmptyState()
        }
    }

    async removeWebhook(event) {
        const button = event.currentTarget
        const webhookEl = button.closest('.aml-webhook')
        const webhookId = button.dataset.id

        if (!confirm(window.I18n.translations['dashboard.setting.webhook.js.confirm_remove'])) return

        try {
            const response = await fetch(this.addUrlValue, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
                },
                body: JSON.stringify({ id: webhookId })
            })

            if (!response.ok) throw new Error('Network response was not ok')

            webhookEl.remove()
            this.updateEmptyState()
        } catch (error) {
            this.showError(window.I18n.translations['dashboard.setting.webhook.js.failed_to_remove'])
        }
    }

    createLoadingWebhook(url) {
        return `
      <div class="aml-webhook" data-url="${url}">
        <div class="aml-webhook__header">
          <div class="aml-webhook__status">
            <div class="aml-webhook__status-dot aml-webhook__status-dot--pending"></div>
            <div class="aml-webhook__url">${url}</div>
          </div>
          <div class="aml-webhook__actions">
            <button type="button" class="aml-btn aml-btn--ghost" disabled>${window.I18n.translations['dashboard.setting.webhook.js.remove']}</button>
          </div>
        </div>
        <div class="aml-webhook__metrics">
          ${this.createLoadingMetric(window.I18n.translations['dashboard.setting.webhook.js.response_time'], '-')}
          ${this.createLoadingMetric(window.I18n.translations['dashboard.setting.webhook.js.activity'], '-', window.I18n.translations['dashboard.setting.webhook.js.loading'])}
          ${this.createLoadingMetric(window.I18n.translations['dashboard.setting.webhook.js.error_rate'], '-', window.I18n.translations['dashboard.setting.webhook.js.loading'])}
        </div>
      </div>
    `
    }

    createLoadingMetric(title, value, details = '') {
        return `
      <div class="aml-webhook__metric">
        <div class="aml-webhook__metric-header">
          <svg class="aml-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2"/>
          </svg>
          ${title}
        </div>
        <div class="aml-webhook__metric-value">${value}</div>
        ${details ? `<div class="aml-webhook__metric-details">${details}</div>` : ''}
      </div>
    `
    }

    createWebhookHtml(webhook) {
        const errorRateClass = this.getErrorRateClass(webhook.error_rate_percentage)
        const successful = webhook.activity_24_hours.successful.toLocaleString();
        const failed = webhook.activity_24_hours.failed.toLocaleString();
        const successfulFailedText = window.I18n.translations['dashboard.setting.webhook.js.successful_and_failed']
            .replace('%{successful}', successful)
            .replace('%{failed}', failed);

        return `
      <div class="aml-webhook" data-url="${webhook.url}">
        <div class="aml-webhook__header">
          <div class="aml-webhook__status">
            <div class="aml-webhook__status-dot aml-webhook__status-dot--${webhook.http_code === 200 ? 'active' : webhook.http_code === 0 ? 'pending' : 'failed'}"></div>
            <div class="aml-webhook__url">${webhook.url}</div>
          </div>
          <div class="aml-webhook__actions">
            <button type="button" class="aml-btn aml-btn--ghost" data-id="${webhook.id}" data-action="dashboard--webhook_subscriber#open" data-dashboard--webhook_subscriber-target="openButton">
              ${window.I18n.translations['dashboard.setting.webhook.js.configure_events']}
            </button>
            
            <button type="button" class="aml-btn aml-btn--ghost" data-id="${webhook.id}" data-action="dashboard--webhooks#removeWebhook">
              ${window.I18n.translations['dashboard.setting.webhook.js.remove']}
            </button>
          </div>
        </div>
        <div class="aml-webhook__metrics">
          <div class="aml-webhook__metric">
            <div class="aml-webhook__metric-header">
              <i class="fa-regular fa-clock aml-icon aml-icon--clock"></i>
              ${window.I18n.translations['dashboard.setting.webhook.js.response_time']}
            </div>
            <div class="aml-webhook__metric-value">
              ${webhook.response_time_ms}<span class="aml-webhook__metric-unit">ms</span>
            </div>
          </div>

          <div class="aml-webhook__metric">
            <div class="aml-webhook__metric-header">
              <svg class="aml-icon aml-icon--activity" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" stroke-width="2"/>
              </svg>
              ${window.I18n.translations['dashboard.setting.webhook.js.activity']}
            </div>
            <div class="aml-webhook__metric-value">
              ${webhook.activity_24_hours.requests.toLocaleString()}
            </div>
            <div class="aml-webhook__metric-details">
              ${successfulFailedText}
            </div>
          </div>

          <div class="aml-webhook__metric">
            <div class="aml-webhook__metric-header">
              <i class="fa-regular fa-circle-exclamation aml-icon aml-icon--alert-triangle"></i>
              ${window.I18n.translations['dashboard.setting.webhook.js.error_rate']}
            </div>
            <div class="aml-webhook__metric-value ${errorRateClass}">
              ${webhook.error_rate_percentage}%
            </div>
            <div class="aml-webhook__metric-details">${window.I18n.translations['dashboard.setting.webhook.js.last_24_hours']}</div>
          </div>
        </div>
      </div>
    `
    }

    getErrorRateClass(rate) {
        if (rate > 10) return 'aml-webhook__metric-value--danger'
        if (rate > 5) return 'aml-webhook__metric-value--warning'
        return 'aml-webhook__metric-value--success'
    }

    validateUrl(url) {
        try {
            new URL(url)
            return true
        } catch {
            return false
        }
    }

    hasExistingWebhook(url) {
        return Array.from(this.webhookListTarget.querySelectorAll('.aml-webhook__url'))
            .some(el => el.textContent === url)
    }

    showError(message) {
        this.errorTextTarget.textContent = message
        this.errorMessageTarget.classList.remove('hidden')
    }

    hideError() {
        this.errorMessageTarget.classList.add('hidden')
    }

    updateEmptyState() {
        const hasWebhooks = this.webhookListTarget.children.length > 0
        this.emptyStateTarget.classList.toggle('hidden', hasWebhooks)
    }
}